import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import FuyaoziScene from "./scene/fuyaozi-scene";
import FuyaoziChat from "./chat/fuyaozi-chat";
import FuyaoziTicket from "./ticket/fuyaozi-ticket";
import FuyaoziTaboo from "./taboo/fuyaozi-taboo";
import FuyaoziShake from "./shake/fuyaozi-shake";
import ConfirmDialog from "../../dialog/confirm/confirm-dialog";
import DivinationService from "../../../service/divination-service";
import useAuth from "../../../hooks/useAuth";
import { divinationMessages, isPending } from "./fuyaozi-messages";
import "./fuyaozi-ask-page-style.scss";

//問卦（伏爻子）
//
//  一頁兩個功能，用頂部的切換鈕在兩者之間切：
//    chat   問卦（歡迎動畫 → 寫問題 → 覆述動畫 → 扣券 → 搖龜殼 → 斷語）
//    ticket 購買問卦券
//
//  做成同一頁而不是兩個路由：買券是為了繼續問，兩者來回很頻繁，
//  切分頁比在頁面之間跳來跳去順。
//
//  —— 為什麼跟提問（白夜）各自一包 ——
//
//  原本這兩個底部頁籤共用同一支元件，只靠 persona 設定區分「誰回答你」——
//  那時兩邊的畫面確實一模一樣，共用是對的。
//
//  現在不是了。問卦長成一整套儀式：
//    歡迎動畫 → 寫問題 → 覆述問題的動畫 → 扣一張問卦券
//    → 卜卦注意事項 → 用手指搖龜殼六次 → 卜卦動畫（同時跑 AI）
//    → 斷語進聊天室
//  而提問仍然只是對話。硬共用會讓那支元件長滿「只有問卦才要做」的分支。
//
//  所以拆成 pages/fuyaozi-ask/ 與 pages/baiye-question/，連 CSS class
//  也各自一套（.fuyaozi-ask__ / .fuyaozi-chat__ / .fuyaozi-ticket__）——
//  共用 class 等於沒拆。
//
//  —— 券 ——
//  問卦券（type "gua"）只能問伏爻子；問事券（"ask"）只能問白夜。
//  原本是同一個計數器，兩頁拆開之後跟著分開（見 models/user 的說明）。
//
//  —— 目前做到哪 ——
//    ✓ 動畫 1  歡迎
//    ✓ 動畫 2  覆述
//    ✓ 購券與扣券（問卦券 190／590／990）
//    ✗ 卜卦注意事項、搖龜殼、動畫 3、斷卦引擎、聊天室持久化

//—— 過場演出的資料表 ——
//
//  一幕一筆，改台詞或換影片只動這張表（同白夜 bazi-baiye-page 的 SCENES）。
//  演出的機制在 scene/fuyaozi-scene.js —— 它只管「播一支影片、照順序講幾句話、
//  講完讓路」，三幕共用同一支。
//
//  leadIn 是第一句比影片晚多久開口。
//  ⚠️ 兩個值都還沒量過 —— 這台機器沒有 ffmpeg 也沒有 Playwright，抓不到
//  影片的格，所以不知道伏爻子實際在第幾秒抬頭、開口。要對著畫面調。
//  （白夜那邊的 2000 是逐格抓出來的：1.3 秒面無表情、1.8 秒嘴角開始動。）
//
//  ⚠️ 改了 lines 要跑一次 `cd client && npm run font:subset`。
//  字型檔是切過的，只含現有台詞的字；漏跑的話新字會悄悄掉回系統明體。
const SCENES = {
  //進門。每次進問卦頁都播（Kevin 2026-10-07 決定）——
  //  不是「只有第一次」也不是「一天一次」。這一幕是進門的儀式。
  welcome: {
    video: "fuyaozi-1.mp4",
    leadIn: 1200,
    //拆成兩句而不是一句帶 \n：兩句是他「看了你一眼、然後開口」的節奏，
    //  一次全亮就只是一張字卡。
    lines: [{ text: "看你面有難色" }, { text: "看來是有什麼想問的問題吧！" }],
  },

  //送出問題之後。
  //
  //  —— 為什麼不把使用者打的問題原字覆述出來 ——
  //  做過一版：問題裝在橢圓對話框裡（沿用故事頁的 .baiye-story__bubble），
  //  50 字上限、字級與斷行由橢圓的弦寬反推。技術上可行，但：
  //    1. 框裡混著使用者打的字，而劇情字型是 subset 過的（只含我們寫的台詞）
  //       —— 他打的每個字都會掉回系統明體，一句話混兩三種長相。
  //    2. 50 字塞進 7:10 的橢圓要縮到 34px、八行，一大顆蛋佔住整個畫面，
  //       而那一拍要傳達的只是「他聽懂了」。
  //  改成他用自己的話確認，上面兩個問題都不存在，而「被聽懂」的感覺還在。
  restate: {
    video: "fuyaozi-2.mp4",
    leadIn: 1000,
    lines: [
      { text: "原來你想問這個問題啊！" },
      { text: "那現在我就來幫你卜個卦吧！" },
    ],
  },

  //六爻搖完 → 伏爻子開始斷卦。
  //
  //  —— 這一幕的工作是「蓋住 AI 跑的時間」——
  //  影片 14.1 秒，斷卦引擎實測約 7.4 秒（用 system prompt 之後）。
  //  使用者看到的是他在算，不是一顆轉圈圈的 loading。
  //
  //  —— lines 刻意是空的（Kevin 2026-10-07 決定：這一幕不要台詞）——
  //  前兩幕他在跟你說話，這一幕他在做事。配上字反而會打斷
  //  「他正在專心算」的感覺。
  //
  //  空陣列時 fuyaozi-scene 會**播到影片結束**才離場（見那支的說明）——
  //  那正是這一幕要的，它的價值就在那 14 秒的長度。
  casting: {
    video: "fuyaozi-3.mp4",
    leadIn: 0,
    lines: [],
  },
};

//—— 斷語還沒好時的輪詢 ——
//
//  斷卦是在後端建卦的當下就開跑的（fire-and-forget），而搖六爻約 12 秒、
//  動畫 3 有 14 秒，合計 26 秒的掩護。實測生成約 15 秒，所以走到這裡
//  通常早就好了 —— 這組常數是給例外情況用的（模型慢、重試、網路卡）。
const VERDICT_POLL_MS = 2000;
const VERDICT_MAX_TRIES = 15;

const FuyaoziAskPage = () => {
  const [tab, setTab] = useState("chat");
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();

  //歡迎動畫的狀態就放在這裡、不落任何儲存：元件一掛載就是 false，
  //  離開再回來自然會再播一次。要改成「只播一次」的話才需要 sessionStorage。
  const [welcomeDone, setWelcomeDone] = useState(false);

  //—— 這一層持有的是「卦」，不是「訊息」 ——
  //
  //  訊息由卦推導（divinationMessages），不另外存一份。
  //  存訊息的話，斷語輪詢回來時要去訊息陣列裡找那一則換掉、分頁往前接時
  //  又要處理兩則一組不能拆開 —— 兩件事都在跟「訊息只是卦的投影」對抗。
  //  改成只維護卦的陣列之後，那兩件事各自變成一行。
  //
  //  順序是舊的在前、新的在後，跟畫面由上往下讀的順序一致。
  const [history, setHistory] = useState([]);
  const messages = useMemo(() => history.flatMap(divinationMessages), [history]);

  //歷史拉完了沒。沒拉完就不要畫空狀態 ——
  //  有二十卦的人一進來會先看到「說一件事」再被歷史蓋掉，閃一下很糟。
  const [historyReady, setHistoryReady] = useState(false);
  //往回翻的游標（上一頁最舊那一卦的 createdAt）與「還有沒有更舊的」
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  //—— 一次問卦會經過的三個中間狀態 ——
  //
  //    restating   正在演覆述那一幕（動畫 2）
  //    taboo       覆述演完，占卦禁忌的 dialog 開著，等他確認
  //    pendingAsk  確認了但沒券，問題先寄放在這裡等他買完回來
  //    shaking     券扣了、卦也定了，正在搖龜殼（存的是整卦，不是問題）
  //
  //  前三個都存**問題全文**，null 代表不在那個狀態。
  //  為什麼一路帶著而不是只留在 restating：禁忌確認之後才真的起卦，
  //  而那一刻要把這句話送給後端（後端拿它取用神）。
  const [restating, setRestating] = useState(null);
  const [taboo, setTaboo] = useState(null);
  const [pendingAsk, setPendingAsk] = useState(null);

  //—— 「還需要一張問卦券」的 dialog 開著沒有 ——
  //
  //  跟 pendingAsk 分開成兩個 state，因為它們的生命週期不一樣：
  //  按下「去購買」的那一刻 dialog 就該收掉（它的話已經說完了，
  //  再蓋在價目表上面只是擋路），但問題還要一直留到他買完回來。
  //
  //  原本兩件事共用一個 state，結果是「不清掉才記得住問題」與
  //  「清掉才關得了 dialog」互相打架 —— dialog 就黏在價目表上。
  const [ticketDialog, setTicketDialog] = useState(false);
  //正在搖的那一卦（後端回來的 divination）。null 代表沒有在搖。
  const [shaking, setShaking] = useState(null);
  //搖完了、正在演斷卦那一幕（動畫 3）的那一卦
  const [casting, setCasting] = useState(null);
  const [askError, setAskError] = useState("");

  //—— 這一層不再持有券的餘額 ——
  //  送出不看餘額（見 handleSend），購券分頁自己會取一次 ——
  //  留一份在這裡只會變成兩個地方各有一個可能過期的數字。
  //  之後「卜卦注意事項」要顯示「將扣 1 張、剩 N 張」時再加回來。

  //切到購券分頁要回到頂端。
  //  問卦分頁進場會自動捲到最新訊息，不重置的話切過來時頁面還停在下面，
  //  「購買問卦券」的標題會被頂部列蓋住。
  useEffect(() => {
    if (tab === "ticket") window.scrollTo(0, 0);
  }, [tab]);

  const handleBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate("/", { replace: true });
  };

  //送出問題 → 直接演覆述那一幕（動畫 2）。
  //
  //  —— 這裡刻意不檢查有沒有券 ——
  //  他剛打完一個很具體的問題，那一刻把他丟去購券頁，等於在說
  //  「先付錢我才聽你講」。照情境的順序：先讓伏爻子聽懂、接過去，
  //  需要券是下一拍的事（見 handleRestated）。
  //
  //  券也是在覆述演完之後才扣 —— 使用者在那幾秒內切走、或 App 被系統
  //  收掉的話，券不該就這樣沒了。最壞的情況是「看了動畫但沒問到」。
  const handleSend = (text) => {
    setAskError("");
    setRestating(text);
  };

  //覆述演完 → 跳占卦禁忌。
  //  這裡**還不扣券**，他看完那幾條按下去才算數。
  const handleRestated = useCallback(() => {
    setTaboo(restating);
    setRestating(null);
  }, [restating]);

  //—— 扣一張券、建一筆卦 ——
  //
  //  抽成一支是因為它有兩個進入點：
  //    1. 禁忌確認之後（正常路徑）
  //    2. 沒券 → 去買 → 買完回來自動接續（見 handleBought）
  //  兩邊要做的事一模一樣，各寫一份遲早會分岔。
  const startDivination = useCallback((question) => {
    if (!question) return;

    //—— 先進搖卦那一幕，再等 API ——
    //
    //  後端這一支要做的事不少（原子扣券 → 排盤 → 寫資料庫），網路慢或
    //  冷啟動時會等上一兩秒。如果等回來才切畫面，使用者按完「起卦」
    //  會先看到聊天室沒有任何反應，以為按鈕壞了。
    //
    //  所以先把那一幕的**背景**放上去（龜殼還不出現，見 FuyaoziShake），
    //  等待就被藏進轉場裡了。{} 是佔位用的：它沒有 gua，
    //  那一幕會自己進入「備卦中」的樣子。
    setShaking({});

    DivinationService.create(question)
      .then(({ data }) => {
        if (!data?.status) {
          //沒券：退回聊天室，再用 dialog 說一聲 ——
          //  他剛按完「我已詳閱，起卦」，畫面無預警換成價目表會很突兀。
          //  問題寄放在 pendingAsk，買完券就是靠它接回來的。
          setShaking(null);
          setPendingAsk(question);
          setTicketDialog(true);
          return;
        }

        //卦已經定好了（後端扣券時就擲完六爻）→ 龜殼浮出來，可以搖了。
        //  他搖的六下是揭露，不是決定（見 shake/fuyaozi-shake 的說明）。
        setShaking(data.data.divination);
      })
      .catch((e) => {
        setShaking(null);
        setAskError(e.response?.data?.message || "起卦失敗，請稍後再試");
      });
  }, []);

  //禁忌確認 → 起卦
  const handleTabooConfirm = useCallback(() => {
    const question = taboo;
    setTaboo(null);
    startDivination(question);
  }, [taboo, startDivination]);

  //「去購買」→ 收掉 dialog、切到購券分頁。
  //
  //  **dialog 關掉，但 pendingAsk 留著** —— 那句問題要等他買完才接得回來。
  //  （綠界開在新分頁，所以這一頁不會被毀掉，state 活得下來。
  //    整頁跳轉的時代做不到這件事。）
  const handleGoBuy = () => {
    setTicketDialog(false);
    setTab("ticket");
  };

  //—— 自己按頁籤切換 ——
  //
  //  手動切頁籤就代表那次「被券擋下來」的交棒結束了，寄放的問題要丟掉。
  //  不丟的話：他被帶去購券頁、自己切回對話放棄了、過幾天又切去買券囤著，
  //  買完會被硬拉去搖那句早就忘了的問題。
  //
  //  被帶過去的那條路走的是 handleGoBuy，不經過這裡。
  const handleSwitchTab = (next) => {
    setPendingAsk(null);
    setTab(next);
  };

  //六爻搖完 → 演斷卦那一幕（動畫 3）。
  //
  //  ⚠️ 這一幕本來要同時觸發 AI 斷卦（影片的 14 秒就是為了蓋住那段時間），
  //  但斷卦引擎還沒寫 —— 所以現在純粹是演出，沒有東西在背景跑。
  //  引擎做好之後在這裡發請求，演完就有斷語可以貼。
  const handleShaken = useCallback(() => {
    setCasting(shaking);
    setShaking(null);
  }, [shaking]);

  //—— 把斷語接回聊天室 ——
  //
  //  先放一則「正在細看」的暫代訊息，拿到斷語再就地換掉它。
  //  為什麼不等拿到才貼：動畫 3 一演完畫面就回到聊天室，那一刻如果
  //  什麼都沒有，看起來像卦問完了卻沒有下文。
  const fillVerdict = useCallback((divinationID, tries = 0) => {
    const retry = () => {
      if (tries < VERDICT_MAX_TRIES) {
        setTimeout(() => fillVerdict(divinationID, tries + 1), VERDICT_POLL_MS);
      }
    };

    DivinationService.get(divinationID)
      .then(({ data }) => {
        const d = data?.data?.divination;

        //還在跑 → 下一輪再問。用 isPending 而不是只看 status，
        //  不然 done 但 verdict 形狀不對的那種會被當成還在跑。
        if (!d || isPending(d)) return retry();

        //  就地把那一卦換成新的 —— 訊息是從卦推導的，換掉卦，
        //  那一組問答自己就會從「容我細看片刻」變成完整的斷語。
        setHistory((prev) =>
          prev.map((x) => (x.divinationID === divinationID ? d : x)),
        );
      })
      //查不到或網路斷一下都不要停，下一輪再試
      .catch(retry);
  }, []);

  //—— 進頁面拉聊天串 ——
  //
  //  訊息沒有存在前端任何地方：重整、換裝置、換瀏覽器都要看得到同一串，
  //  所以唯一的來源是 D-1。本機存一份只會變成「這台看得到、那台看不到」。
  //
  //  未登入不打（D-1 要登入）。登入攔截在輸入框 focus 的時候才跳 ——
  //  先讓他進來看到角色，所以這裡要能安靜地什麼都不做。
  //  登入完成之後 isLoggedIn 會翻，這支 effect 會自己再跑一次把歷史補上。
  useEffect(() => {
    if (!isLoggedIn) {
      setHistory([]);
      setHasMore(false);
      setHistoryReady(true);
      return;
    }

    //  alive 要在 effect 裡面設成 true，不能寫 useRef(true)。
    //  StrictMode 下是「掛載 → cleanup → 再掛載」，而 useRef 的初始值
    //  第二次掛載不會重新執行 —— 那樣第二次之後永遠是 false（CLAUDE.md）。
    let alive = true;

    DivinationService.list()
      .then(({ data }) => {
        if (!alive) return;
        const list = data?.data?.divinations || [];

        //  後端是「新的在前」（往回翻才撈得動），
        //  但聊天室是由上往下讀時間，所以這裡翻過來。
        const ordered = [...list].reverse();
        setHistory(ordered);
        setCursor(data?.data?.nextBefore || null);
        setHasMore(Boolean(data?.data?.hasMore));

        //  離開時還在生成的那幾卦要接著輪詢 ——
        //  不接的話它們會永遠停在「容我細看片刻」，而斷語其實早就寫好了。
        ordered.filter(isPending).forEach((d) => fillVerdict(d.divinationID));
      })
      //拉不到就當作沒有歷史。這一頁的主要功能是問新的一卦，
      //  為了歷史擋住整頁不值得。
      .catch(() => {})
      .finally(() => {
        if (alive) setHistoryReady(true);
      });

    return () => {
      alive = false;
    };
  }, [isLoggedIn, fillVerdict]);

  //—— 往回翻一頁（捲到最上面時觸發）——
  //
  //  接在**最前面**，而且捲軸位置要留在原地 —— 那一段在聊天室元件裡做
  //  （只有它知道畫面高度變了多少）。
  const handleLoadMore = useCallback(() => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);

    DivinationService.list(cursor)
      .then(({ data }) => {
        const list = data?.data?.divinations || [];
        const ordered = [...list].reverse();
        setHistory((prev) => [...ordered, ...prev]);
        setCursor(data?.data?.nextBefore || null);
        setHasMore(Boolean(data?.data?.hasMore));
        ordered.filter(isPending).forEach((d) => fillVerdict(d.divinationID));
      })
      //  翻不動就讓它停在那裡。往回看是附加的，失敗不該影響問新的一卦。
      .catch(() => setHasMore(false))
      .finally(() => setLoadingMore(false));
  }, [cursor, loadingMore, fillVerdict]);

  //斷卦那一幕演完 → 問題與斷語都進對話
  const handleCast = useCallback(() => {
    const divination = casting;
    setCasting(null);
    if (!divination) return;

    //  剛建好的那一卦斷語一定還沒好（後端是 fire-and-forget），
    //  所以它會先投影成「問題 + 容我細看片刻」兩則，等輪詢換掉。
    setHistory((prev) => [...prev, divination]);
    fillVerdict(divination.divinationID);
  }, [casting, fillVerdict]);

  //買完券（券真的入帳）→ 回到對話，並且自動接續原本那一卦。
  //
  //  使用者是為了問那一句話才去買券的，回來還要他重打一次很荒謬。
  const handleBought = useCallback(() => {
    const question = pendingAsk;

    //—— 自己來買券的，買完就留在購券頁 ——
    //
    //  只有「問到一半被券擋下來」的人才該被帶走（他有一句話寄放在
    //  pendingAsk，那就是他被帶來這裡的原因）。
    //
    //  自己從頁籤切過來囤券的人沒有未完成的事，把他彈回對話等於
    //  替他決定接下來要做什麼 —— 而他可能只是想再買一包。
    if (!question) return;

    setPendingAsk(null);
    setTab("chat");

    //  不用再跳一次禁忌：他剛才已經確認過了，中間只是繞去付錢。
    //  startDivination 會立刻把搖卦那一幕的背景放上去，所以買完的下一眼
    //  就是龜殼，不會先閃過一次空的聊天室。
    //  問題本身在後端扣券的當下就寫進這一卦了，搖完、斷完會跟著斷語
    //  一起進聊天串（見 handleCast）。
    startDivination(question);
  }, [pendingAsk, startDivination]);

  return (
    <main className="fuyaozi-ask">
      <header className="fuyaozi-ask__header">
        <button
          type="button"
          className="fuyaozi-ask__back"
          onClick={handleBack}
          aria-label="返回"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path
              d="M15 5l-7 7 7 7"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="fuyaozi-ask__switch" role="tablist" aria-label="問卦功能">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "ticket"}
            aria-label="購買問卦券"
            className={
              tab === "ticket"
                ? "fuyaozi-ask__switch-btn is-active"
                : "fuyaozi-ask__switch-btn"
            }
            onClick={() => handleSwitchTab("ticket")}
          >
            {/* 票券。兩側的半圓凹口加中間那排虛線（撕票的齒孔）——
                凹口自己就認得出是票，但在 20px 下只剩一個圓角方塊，
                虛線是讓它一眼看出來的那一筆。
                凹口半徑取 2 不取 3：3 在 13 單位高的票上會吃掉整個高度，
                兩個凹口幾乎對穿，整個圖形變成線軸。 */}
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <g
                stroke="currentColor"
                strokeWidth="1.8"
                fill="none"
                strokeLinejoin="round"
                strokeLinecap="round"
              >
                <path d="M2.5 10V7.5a2 2 0 012-2h15a2 2 0 012 2V10a2 2 0 000 4v2.5a2 2 0 01-2 2h-15a2 2 0 01-2-2V14a2 2 0 000-4z" />
                <path d="M15 7v1.6M15 11.2v1.6M15 15.4v1.6" />
              </g>
            </svg>
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={tab === "chat"}
            aria-label="問卦"
            className={
              tab === "chat"
                ? "fuyaozi-ask__switch-btn is-active"
                : "fuyaozi-ask__switch-btn"
            }
            onClick={() => handleSwitchTab("chat")}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path
                d="M20 15a3 3 0 01-3 3H9l-4 3v-3H7a3 3 0 01-3-3V7a3 3 0 013-3h10a3 3 0 013 3z"
                stroke="currentColor"
                strokeWidth="1.8"
                fill="none"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* 佔位讓切換鈕真正置中，不被返回鍵擠偏 */}
        <span className="fuyaozi-ask__spacer" aria-hidden="true" />
      </header>

      {tab === "chat" ? (
        <FuyaoziChat
          messages={messages}
          onSend={handleSend}
          error={askError}
          ready={historyReady}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={handleLoadMore}
        />
      ) : (
        //  買完券（真的入帳）就切回問卦，並接續原本那一卦 ——
        //  綠界開在新分頁，所以這一頁還活著，他剛打的問題也還在。
        <FuyaoziTicket onBought={handleBought} />
      )}

      {/* 占卦禁忌：覆述演完跳出來，確認過才真的扣券起卦。
          「再想想」直接關掉 —— 這一刻還沒扣券，退出去不會有損失。 */}
      <FuyaoziTaboo
        open={taboo !== null}
        onConfirm={handleTabooConfirm}
        onCancel={() => setTaboo(null)}
      />

      {/* 沒券：先說一聲，不要讓畫面無預警換成價目表。
          沿用通用的 ConfirmDialog —— 它就是「一段話 + 兩顆鈕」，
          另外做一支只會多一份要維護的樣式。 */}
      <ConfirmDialog
        open={ticketDialog}
        title="還需要一張問卦券"
        message="伏爻子已經接下你的問題，但手邊沒有可用的問卦券了。買一張就能立刻起卦。"
        confirmTxt="去購買"
        cancelTxt="先不要"
        onSuccess={handleGoBuy}
        //  「先不要」或點外面關掉才是真的放棄 —— 這時候問題可以丟掉。
        //  按「去購買」走的是 onSuccess，不會經過這裡（它留著問題）。
        onClose={() => {
          setTicketDialog(false);
          setPendingAsk(null);
        }}
      />

      {/* 搖龜殼。扣完券、卦定好之後的那一幕，六爻揭露完才往下走。 */}
      {shaking && (
        <FuyaoziShake gua={shaking.gua} onDone={handleShaken} />
      )}

      {/* 過場蓋在整頁之上（z-index 300，壓過上面那個 sticky 的 200）。
          放在最後而不是最前：DOM 順序就是堆疊順序，
          擺在前面的話即使 z-index 夠高，也還得跟 sticky 的頂部列競爭。

          演完才卸載 —— 留著一個 opacity: 0 的 <video> 在那裡，
          iOS 會繼續把它當成一個合成層養著。

          key 綁幕名：兩幕共用同一支元件，不給 key 的話 React 會沿用
          同一個實例（台詞進度、playing 都是舊的），第二幕會從半路開始演。

          歡迎動畫只在問卦分頁播 —— 在購券分頁蓋一層影片上去很怪，
          而且使用者是為了買券才切過去的。 */}
      {tab === "chat" && !welcomeDone && (
        <FuyaoziScene
          key="welcome"
          scene={SCENES.welcome}
          onDone={() => setWelcomeDone(true)}
        />
      )}

      {welcomeDone && restating !== null && (
        <FuyaoziScene
          key="restate"
          scene={SCENES.restate}
          onDone={handleRestated}
        />
      )}

      {/* 動畫 3：六爻搖完，伏爻子開始斷卦。
          沒有台詞，所以會播到影片結束（14.1 秒）才讓路。 */}
      {casting && (
        <FuyaoziScene
          key="casting"
          scene={SCENES.casting}
          onDone={handleCast}
        />
      )}
    </main>
  );
};

export default FuyaoziAskPage;
